
import React, { useState } from 'react';
import { OrderWithCustomer } from "../types/orderTypes";
import OrdersTable from "./OrdersTable";
import { Button } from "@/components/ui/button";
import { Trash2, Download, PlusCircle } from "lucide-react";
import { exportOrdersToCSV } from "../utils/exportUtils";
import SelectionSummary from './SelectionSummary';
import { Skeleton } from "@/components/ui/skeleton";

interface OrdersListViewProps {
  orders: OrderWithCustomer[];
  loading: boolean;
  selectedOrders: string[];
  toggleOrderSelection: (id: string) => void;
  toggleSelectAll: (orders?: OrderWithCustomer[]) => void;
  areAllSelected: (orders?: OrderWithCustomer[]) => boolean; 
  handleEditOrder: (id: string) => void;
  handleDeleteOrder: (id: string) => void;
  handleBulkDeleteOrders: (ids: string[]) => void;
  formatDate: (date: string) => string;
  formatStatus: (status: string) => { label: string; className: string };
  formatPaymentStatus: (status: string) => { label: string; className: string };
  handleAddOrder?: () => void;
  organizationId?: string | null;
}

const OrdersListView = ({
  orders,
  loading,
  selectedOrders,
  toggleOrderSelection,
  toggleSelectAll,
  areAllSelected,
  handleEditOrder,
  handleDeleteOrder,
  handleBulkDeleteOrders,
  formatDate,
  formatStatus,
  formatPaymentStatus,
  handleAddOrder,
  organizationId
}: OrdersListViewProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtrer les commandes en fonction du terme de recherche
  const filteredOrders = orders.filter(order => {
    if (!searchTerm.trim()) return true;
    
    const search = searchTerm.toLowerCase();
    
    return (
      // Rechercher par numéro de commande
      (order.order_number && order.order_number.toLowerCase().includes(search)) ||
      // Rechercher par nom de client
      (order.customer_name && order.customer_name.toLowerCase().includes(search)) ||
      // Rechercher par statut
      (formatStatus(order.status).label.toLowerCase().includes(search)) ||
      // Rechercher par statut de paiement
      (formatPaymentStatus(order.payment_status).label.toLowerCase().includes(search))
    );
  });

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="p-6 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  // Calculer si tous les ordres sont sélectionnés
  const allFilteredOrdersSelected = filteredOrders.length > 0 && areAllSelected(filteredOrders);

  // Fonction pour basculer la sélection de tous les ordres filtrés
  const handleToggleSelectAll = () => {
    toggleSelectAll(filteredOrders);
  };

  return (
    <>
      {selectedOrders.length > 0 && (
        <SelectionSummary 
          selectedCount={selectedOrders.length} 
          totalCount={filteredOrders.length}
          onDelete={() => handleBulkDeleteOrders(selectedOrders)}
          isDeleting={false}
        />
      )}
      
      <div className="shadow-sm border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-lg overflow-hidden">
        <div className="p-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Commandes ({filteredOrders.length})</h3>
          <div className="flex gap-2">
            <Button 
              onClick={handleAddOrder}
              className="add-button"
              size="sm"
              title="Ajouter une commande"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Ajouter une commande
            </Button>
          </div>
        </div>
      
        <OrdersTable 
          orders={filteredOrders}
          selectedOrders={selectedOrders}
          toggleOrderSelection={toggleOrderSelection}
          toggleSelectAll={handleToggleSelectAll}
          areAllSelected={allFilteredOrdersSelected}
          handleEditOrder={handleEditOrder}
          handleDeleteOrder={handleDeleteOrder}
          formatDate={formatDate}
          formatStatus={formatStatus}
          formatPaymentStatus={formatPaymentStatus}
        />
      </div>
    </>
  );
};

export default OrdersListView;
